import { API_BASE_URL } from '../configuration/config';
import { BaseService } from './base-service';
import { StatusCodes } from 'http-status-codes';

export interface BackfillRunCounts {
  total: number;
  imported: number;
  approved: number;
  pendingReview: number;
  importFailed: number;
  existing: number;
  declined: number;
}

export interface BackfillRun {
  jobId: string;
  source: string;
  destination: string;
  configId: string;
  createdAt: string;
  status: string;
  counts: BackfillRunCounts;
}

export interface GetRunsResponse {
  runs: BackfillRun[];
}

/** Frozen API review-state enum values. */
export enum BackfillReviewState {
  APPROVED = 'APPROVED',
  PENDING_REVIEW = 'PENDING_REVIEW',
  EXISTING = 'EXISTING',
  DECLINED = 'DECLINED',
  IMPORT_FAILED = 'IMPORT_FAILED'
}

/** Item-level import status values returned by the API. */
export enum BackfillItemStatus {
  IMPORTED = 'IMPORTED',
  IMPORT_FAILED = 'IMPORT_FAILED',
  EXISTING = 'EXISTING'
}

/** Item-level approval status values returned by the API. */
export enum BackfillApprovalStatus {
  APPROVED = 'APPROVED',
  PENDING_REVIEW = 'PENDING_REVIEW',
  DECLINED = 'DECLINED'
}

/** Run-level status values. */
export enum BackfillRunStatus {
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
  IN_PROGRESS = 'IN_PROGRESS',
  RUNNING = 'RUNNING'
}

export type ReviewState = `${BackfillReviewState}`;

export interface ValidationVerdict {
  verdict?: string;
  reason?: string;
  violations?: string[];
}

export interface MembershipVerdict extends ValidationVerdict {
  matchedName?: string;
  taxonomyId?: string;
  score?: number;
}

export interface ItemValidation {
  membership?: MembershipVerdict;
  accuracy?: ValidationVerdict;
  classification?: ValidationVerdict;
  style?: ValidationVerdict;
}

export interface ItemImportResult {
  status: string;
  taxonomyId?: string;
  error?: string;
}

export interface ItemSources {
  internal: string[];
  web: string[];
}

export interface BackfillItem {
  sourceSourceId: string;
  itemId: string;
  name: string;
  source: string;
  vendor?: string;
  subtype?: string;
  status: string;
  approvalStatus: string | null;
  confidenceScore: { total: number } | null;
  validation: ItemValidation | null;
  importResult: ItemImportResult | null;
  generatedDescription?: string;
  sources?: ItemSources;
  updatedAt: string;
}

export interface GetRunItemsResponse {
  items: BackfillItem[];
  totalCount: number;
  truncated?: boolean;
}

export class ContentBackfillService extends BaseService {
  public static instance = new ContentBackfillService(API_BASE_URL);

  public async getRuns(): Promise<GetRunsResponse> {
    const response = await this.fetch('/content-backfill/runs');
    if (response.status !== StatusCodes.OK) {
      throw new Error(
        `An error occurred when fetching content backfill runs: ${response.status}`
      );
    }
    return await response.json();
  }

  public async getRunItems(runId: string): Promise<GetRunItemsResponse> {
    const path = `/content-backfill/runs/${runId}/items`;
    const response = await this.fetch(path);
    if (response.status !== StatusCodes.OK) {
      throw new Error(
        `An error occurred when fetching content backfill run items: ${response.status}`
      );
    }
    return await response.json();
  }
}
